import React from "react";
import A4PageFrame from "./A4PageFrame";
import { ROWS_PER_PAGE_PORTRAIT, ROW_HEIGHT } from "./pageMetrics";
import { fmtNum, fmtPct } from "./formatters";

// Phase 3 (B4a): Compteurs (Meters) — mirrors PDF pages 14–16.
//
// Per-meter row: Numéro, Désignation, Energie, Compteur, current value, Unit,
// prior value, Diff., %, Info. Per client direction (Q2): no anomaly flagging —
// the raw % is shown even when extreme (we saw +14627% etc. in Etang B). Meters
// are NOT subtotalled (each meter is independent).
//
// Pagination: ~46 rows per A4 page. The final page also carries the two charts.
//
// Props
//   data            response from GET /v1/repartition-report/meters
//   periodYears     { current, prior? } — chart legend
//   compareEnabled  bool

// The pinned `lineHeight` on the `td` style below keeps every row a
// deterministic 19 px, which is what actually prevents the trailing rows from
// being cropped out of the html-to-image capture between pages. The row COUNT is
// derived from the calibrated portrait page geometry (see pageMetrics.js) —
// 46 rows, a one-row cushion below the 48-row count that previously clipped the
// 71-meter Plaines 6 report — instead of a hand-tuned magic number. Single
// source of truth shared with the METERS_ROWS_PER_PAGE mirror in Preview.js, so
// the "Page X/N" footer can never drift from the actual page break.
const ROWS_PER_PAGE = ROWS_PER_PAGE_PORTRAIT;

const MetersComparison = ({
  data,
  building,
  periodLabel,
  periodYears,
  compareEnabled,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const meters = data?.meters || [];
  const currentYear = periodYears?.current ?? "Current";
  const priorYear = periodYears?.prior ?? "Prior";

  const pages = [];
  for (let i = 0; i < meters.length; i += ROWS_PER_PAGE) {
    pages.push(meters.slice(i, i + ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  // Bar chart: aggregate by energy bucket (Electricité, Eau froide, Chaud, ECS,
  // Pass #3: the client asked to DELETE the Compteurs charts. This section is now
  // table pages ONLY (no trailing "Synthèse" page). metersPages in Preview.js must
  // match (no +1).
  const totalSectionPages = pages.length;

  return (
    <>
      {pages.map((rows, pageIdx) => {
        const pageKey = rows[0]?.consumption ?? `empty-${pageIdx}`;
        return (
          <A4PageFrame
            key={`m-${pageKey}`}
            building={building}
            periodLabel={periodLabel}
            sectionTitle={`Compteurs (${pageIdx + 1}/${totalSectionPages})`}
            whiteLabelling={whiteLabelling}
            logoUrl={logoUrl}
            pageNumber={basePageNumber + pageIdx}
            totalPages={grandTotalPages}
          >
            <table style={tbl}>
              <thead>
                {/* M1.3: column structure aligned to the standardised report
                  layout confirmed by the client on 10.06.2026. Changes:
                    - Reading column header: "31.12.YYYY" → "Lecture"
                      (semantically the prior-period end reading)
                    - Unité moved from position 6 to position 7 (after the
                      current-year value column)
                    - "Diff." header → "Différence"
                    - "Energie" → "Énergie" (accent restored) */}
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th}>Numéro</th>
                  <th style={th}>Désignation</th>
                  <th style={th}>Énergie</th>
                  <th style={th}>Compteur</th>
                  <th style={thNum}>Lecture</th>
                  <th style={thNum}>{currentYear}</th>
                  <th style={th}>Unité</th>
                  {compareEnabled && <th style={thNum}>{priorYear}</th>}
                  {compareEnabled && <th style={thNum}>Différence</th>}
                  {compareEnabled && <th style={thNum}>%</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.consumption}>
                    <td style={td}>{m.consumption}</td>
                    <td style={td}>{m.name || ""}</td>
                    <td style={td}>{m.energie || ""}</td>
                    <td style={td}>{m.compteur || ""}</td>
                    <td style={tdNum}>{m.current_reading != null ? fmtNum(m.current_reading) : ""}</td>
                    <td style={tdNum}>{fmtNum(m.main_value)}</td>
                    <td style={td}>{m.unit || ""}</td>
                    {compareEnabled && <td style={tdNum}>{fmtNum(m.compare_value)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtNum(m.diff)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtPct(m.diff_pct)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </A4PageFrame>
        );
      })}
    </>
  );
};

// marginBottom:0 overrides Bootstrap Reboot's `table{margin-bottom:1rem}` so the
// fixed content region keeps its full height (see pageMetrics.js).
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 0 };
// M1.1: bumped from 9/8.5pt to 11/10pt per client feedback.
const th = { padding: "3px 4px", fontWeight: 600, textAlign: "left", fontSize: 11 };
const thNum = { ...th, textAlign: "right" };
// Plaines-6 fix: pin lineHeight so row height is deterministic (regardless of PP
// Neue Montreal metrics) — this is what actually prevents the trailing rows from
// being clipped out of the html-to-image capture between pages.
const td = {
  // Row height PINNED to ROW_HEIGHT.portrait so no meter row drifts taller and
  // gets clipped at a page boundary (recurring clipping bug on the 71-meter
  // Plaines 6 report). nowrap + overflow:hidden keeps long names single-line.
  boxSizing: "border-box",
  height: ROW_HEIGHT.portrait,
  padding: "2px 4px",
  fontSize: 10,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default MetersComparison;
